/**
 * KI-Marketing Dashboard - Automatische Kundenbindung
 * 
 * Features:
 * - Automatische Erkennung von Kunden-Inaktivität
 * - KI-generierte personalisierte E-Mails
 * - Admin-Kontrolle (an/aus, Frequenz, Tonalität)
 * 
 * Erfolgskriterium: "Der Betreiber merkt nur, dass Kunden öfter wiederkommen – ohne Arbeit."
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Mail, Users, TrendingUp, Settings, 
  AlertTriangle, Check, Clock, Send,
  UserPlus, UserMinus, Crown, Heart,
  Sparkles, BarChart3, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status Colors
const STATUS_COLORS = {
  active: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Aktiv' },
  at_risk: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Gefährdet' },
  inactive: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Inaktiv' },
  churned: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Abgewandert' }
};

// Trigger Icons
const TRIGGER_ICONS = {
  welcome: UserPlus,
  reactivation: UserMinus,
  vip: Crown,
  loyalty: Heart,
  win_back: Sparkles
};

// Customer Card Component
function CustomerCard({ customer, onSendEmail }) {
  const status = STATUS_COLORS[customer.status] || STATUS_COLORS.active;
  const TriggerIcon = TRIGGER_ICONS[customer.suggested_email_type] || Mail;
  
  return (
    <Card className="bg-slate-800 border-slate-700 p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-white">{customer.name || customer.email}</span>
            <Badge className={`${status.bg} ${status.text} border-0`}>
              {status.label}
            </Badge>
          </div>
          
          <div className="grid grid-cols-3 gap-4 text-sm mb-3">
            <div>
              <p className="text-slate-400">Bestellungen</p>
              <p className="text-white font-medium">{customer.total_orders}</p>
            </div>
            <div>
              <p className="text-slate-400">Umsatz</p>
              <p className="text-white font-medium">€{customer.total_spent?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-slate-400">Letzte Bestellung</p>
              <p className="text-white font-medium">
                {customer.days_since_last_order !== null 
                  ? `Vor ${customer.days_since_last_order} Tagen`
                  : '-'}
              </p>
            </div>
          </div>
          
          {customer.suggested_action && (
            <div className="flex items-center gap-2 text-sm text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              {customer.suggested_action}
            </div>
          )}
        </div>
        
        {customer.suggested_email_type && (
          <Button
            size="sm"
            onClick={() => onSendEmail(customer.customer_id, customer.suggested_email_type)}
            className="bg-gradient-to-r from-purple-500 to-pink-500"
          >
            <TriggerIcon className="w-4 h-4 mr-1" />
            Mail senden
          </Button>
        )}
      </div>
    </Card>
  );
}

// Stats Card Component
function StatsCard({ icon: Icon, label, value, color }) {
  return (
    <Card className={`bg-slate-800 border-slate-700 p-4`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 ${color} rounded-xl`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-slate-400">{label}</p>
        </div>
      </div>
    </Card>
  );
}

export default function AIMarketing() {
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({
    enabled: true,
    tone: 'neutral',
    max_emails_per_customer_per_month: 2,
    welcome_email: true,
    reactivation_email: true,
    reactivation_days: 30,
    vip_email: true,
    vip_threshold: 100,
    loyalty_email: true,
    loyalty_orders: 10
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  const sessionToken = localStorage.getItem('access_token') || localStorage.getItem('access_token');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Dashboard
        const dashRes = await fetch(`${BACKEND_URL}/api/ai-marketing/dashboard`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        if (dashRes.ok) {
          const dashData = await dashRes.json();
          setDashboard(dashData);
          setSettings(dashData.settings);
        }
        
        // Customers
        const custRes = await fetch(`${BACKEND_URL}/api/ai-marketing/customers/analysis`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        });
        if (custRes.ok) {
          const custData = await custRes.json();
          setCustomers(custData.customers || []);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (sessionToken) loadData();
  }, [sessionToken]);

  // Save settings
  const saveSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ai-marketing/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Einstellungen gespeichert!');
      }
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Send email
  const sendEmail = async (customerId, triggerType) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ai-marketing/campaigns/send?customer_id=${customerId}&trigger_type=${triggerType}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${sessionToken}` }
        }
      );
      
      if (response.ok) {
        toast.success('E-Mail wird gesendet!');
      }
    } catch (error) {
      toast.error('Fehler beim Senden');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Laden...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">KI-Marketing</h1>
              <p className="text-slate-400">Automatische Kundenbindung ohne Aufwand</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-slate-400">Marketing</span>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => {
                setSettings({ ...settings, enabled: checked });
                saveSettings();
              }}
            />
            <span className={settings.enabled ? 'text-emerald-400' : 'text-slate-500'}>
              {settings.enabled ? 'Aktiv' : 'Deaktiviert'}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-slate-700">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-slate-700">
              <Users className="w-4 h-4 mr-2" />
              Kunden
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-slate-700">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <StatsCard
                icon={Users}
                label="Kunden gesamt"
                value={dashboard?.stats?.total_customers || 0}
                color="bg-blue-500"
              />
              <StatsCard
                icon={UserMinus}
                label="Inaktive Kunden"
                value={dashboard?.stats?.inactive_customers || 0}
                color="bg-orange-500"
              />
              <StatsCard
                icon={Send}
                label="E-Mails diesen Monat"
                value={dashboard?.stats?.campaigns_this_month || 0}
                color="bg-purple-500"
              />
              <StatsCard
                icon={TrendingUp}
                label="Aktionen nötig"
                value={dashboard?.suggested_actions?.length || 0}
                color="bg-amber-500"
              />
            </div>

            {/* Suggested Actions */}
            {dashboard?.suggested_actions?.length > 0 && (
              <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20 p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  KI-Empfehlungen
                </h3>
                <div className="space-y-3">
                  {dashboard.suggested_actions.map((action, idx) => (
                    <div 
                      key={idx}
                      className={`p-4 rounded-xl ${
                        action.priority === 'high' ? 'bg-red-500/10' : 'bg-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-white">{action.title}</h4>
                          <p className="text-sm text-slate-400">{action.description}</p>
                        </div>
                        <Button size="sm" variant="outline" className="border-slate-600">
                          Aktion starten
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* How it works */}
            <Card className="bg-slate-800 border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">So funktioniert KI-Marketing</h3>
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <UserPlus className="w-6 h-6 text-blue-400" />
                  </div>
                  <h4 className="font-medium text-white mb-1">Willkommen</h4>
                  <p className="text-sm text-slate-400">Erste Bestellung → Begrüßungs-Mail</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-6 h-6 text-orange-400" />
                  </div>
                  <h4 className="font-medium text-white mb-1">Reaktivierung</h4>
                  <p className="text-sm text-slate-400">30+ Tage inaktiv → "Wir vermissen Sie"</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Crown className="w-6 h-6 text-amber-400" />
                  </div>
                  <h4 className="font-medium text-white mb-1">VIP</h4>
                  <p className="text-sm text-slate-400">Große Bestellung → Dankes-Mail</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Heart className="w-6 h-6 text-pink-400" />
                  </div>
                  <h4 className="font-medium text-white mb-1">Treue</h4>
                  <p className="text-sm text-slate-400">10+ Bestellungen → Belohnungs-Mail</p>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Kundenanalyse ({customers.length} Kunden)
              </h3>
            </div>
            
            {customers.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700 p-12 text-center">
                <Users className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">Noch keine Kundendaten vorhanden</p>
                <p className="text-sm text-slate-500">Kunden werden automatisch erfasst sobald sie bestellen</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {customers.map((customer) => (
                  <CustomerCard
                    key={customer.customer_id}
                    customer={customer}
                    onSendEmail={sendEmail}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card className="bg-slate-800 border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Marketing-Einstellungen</h3>
              
              <div className="space-y-6">
                {/* Tone */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Tonalität
                  </label>
                  <div className="flex gap-3">
                    {['locker', 'neutral', 'formell'].map((tone) => (
                      <Button
                        key={tone}
                        variant={settings.tone === tone ? 'default' : 'outline'}
                        onClick={() => setSettings({ ...settings, tone })}
                        className={settings.tone === tone ? 'bg-purple-500' : 'border-slate-600'}
                      >
                        {tone === 'locker' && '😊 Locker'}
                        {tone === 'neutral' && '🤝 Neutral'}
                        {tone === 'formell' && '👔 Formell'}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Max. E-Mails pro Kunde/Monat
                  </label>
                  <div className="flex gap-3">
                    {[1, 2, 3, 4].map((num) => (
                      <Button
                        key={num}
                        variant={settings.max_emails_per_customer_per_month === num ? 'default' : 'outline'}
                        onClick={() => setSettings({ ...settings, max_emails_per_customer_per_month: num })}
                        className={settings.max_emails_per_customer_per_month === num ? 'bg-purple-500' : 'border-slate-600'}
                      >
                        {num}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Triggers */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-4">
                    Automatische Trigger
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <UserPlus className="w-5 h-5 text-blue-400" />
                        <span className="text-white">Willkommens-Mail bei erster Bestellung</span>
                      </div>
                      <Switch
                        checked={settings.welcome_email}
                        onCheckedChange={(checked) => setSettings({ ...settings, welcome_email: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-orange-400" />
                        <span className="text-white">Reaktivierungs-Mail nach {settings.reactivation_days} Tagen</span>
                      </div>
                      <Switch
                        checked={settings.reactivation_email}
                        onCheckedChange={(checked) => setSettings({ ...settings, reactivation_email: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Crown className="w-5 h-5 text-amber-400" />
                        <span className="text-white">VIP-Mail bei Bestellung über €{settings.vip_threshold}</span>
                      </div>
                      <Switch
                        checked={settings.vip_email}
                        onCheckedChange={(checked) => setSettings({ ...settings, vip_email: checked })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <Heart className="w-5 h-5 text-pink-400" />
                        <span className="text-white">Treue-Mail nach {settings.loyalty_orders} Bestellungen</span>
                      </div>
                      <Switch
                        checked={settings.loyalty_email}
                        onCheckedChange={(checked) => setSettings({ ...settings, loyalty_email: checked })}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={saveSettings}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Einstellungen speichern
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
